import { GridRowId } from '@mui/x-data-grid';
import { get, post, put, del } from '../../common/axiosClient';
import { API } from '../../common/constants/urls';
import {
  CA_REPORT,
  ReportLineItem,
  UpdateReportLineItemRequest,
  UpdateReportRequest,
  RecentlyUploadedReportsDTO,
  UpdateDivisionProjectDrawsDTO,
  CreateLineItemDTO,
  ReportInfoListResponse,
  ReportGeneralInfo,
  CompsDivisionAveragesDTO,
  generateReportCompsDTO,
  BulkUpdateReportLineItemRequest,
  CAReportInvoiceDTO,
  CreateCAReportInvoiceDTO,
  UpdateCAReportInvoiceDTO,
} from './report.type';
import { NoteDTO } from 'common/utils/util.type';

export const generateReport = async (projectId: string, generalInfoDto: ReportGeneralInfo) => {
  return (await post(`${API.CA_REPORT}/${projectId}`, generalInfoDto)) as CA_REPORT;
};

export const getReport = async (reportId: string) => {
  return (await get(`${API.CA_REPORT}/${reportId}`)) as CA_REPORT;
};

export const updateReport = async (reportId: string, requestDto: UpdateReportRequest) => {
  return (await put(`${API.CA_REPORT}/${reportId}`, requestDto)) as CA_REPORT;
};

export const updateReportLineItem = async (reportlineItemId: string, requestDto: UpdateReportLineItemRequest) => {
  return (await put(`${API.CA_REPORT_LINE_ITEM}/${reportlineItemId}`, requestDto)) as ReportLineItem;
};

export const bulkUpdateReportLineItem = async (requestDto: BulkUpdateReportLineItemRequest) => {
  return (await put(`${API.CA_REPORT_LINE_ITEM}/bulk-edit`, requestDto)) as string;
};

export const deleteReportLineItem = async (reportlineItemId: string) => {
  return (await del(`${API.CA_REPORT_LINE_ITEM}/${reportlineItemId}`)) as string;
};

export const bulkDeleteReportLineItems = async (reportlineItemIds: string[]) => {
  return (await del(`${API.CA_REPORT_LINE_ITEM}/bulk-delete`, {
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(reportlineItemIds),
  })) as string;
};

export const addOrUpdateAdjustment = async (reportId: string) => {
  return (await post(`${API.CA_REPORT}/${reportId}/adjustment-line-item`, {})) as boolean;
};

export const getAllReports = async (queryParams: string[]) => {
  const queryString = queryParams.join('&');
  return (await get(`${API.CA_REPORT}?${queryString}`)) as ReportInfoListResponse;
};

export const getTopFiveRecentlyUploadedReports = async (userId: string | null) => {
  const queryString = userId ? `?userId=${userId}` : '';
  return (await get(`${API.CA_REPORT}/top-five-recent/${queryString}`)) as RecentlyUploadedReportsDTO[];
};
export const fetchDivisionProjects = async (reportId: string) => {
  return await get(`${API.CA_REPORT}/${reportId}/division-project-draws`);
};
export const updateDivisionProjectDraws = async (reportId: string, updateDto: UpdateDivisionProjectDrawsDTO) => {
  return await put(`${API.CA_REPORT}/${reportId}/update-division-projects`, updateDto);
};
export const updateDivisionComps = async (reportId: string, updateDto: UpdateDivisionProjectDrawsDTO) => {
  return await put(`${API.CA_REPORT}/${reportId}/update-division-comps`, updateDto);
};
export const addOrUpdateNoteToReport = async (reportId: string, requestDto: NoteDTO) => {
  return await put(`${API.CA_REPORT}/${reportId}/notes`, requestDto);
};

export interface BulkAddOrUpdateNotesDTO {
  reportId: string;
  notes: NoteDTO[];
  isAutoGenerated?: boolean;
  deletePreviousAutoGeneratedNotes?: boolean;
}

export const addOrUpdateNotesToReport = async (bulkNotesDto: BulkAddOrUpdateNotesDTO) => {
  return await put(`${API.CA_REPORT}/notes/bulk`, bulkNotesDto);
};

export const getNotes = async (reportId: string) => {
  return await get(`${API.CA_REPORT}/${reportId}/notes`);
};
export const deleteNoteFromReport = async (reportId: string, noteId: string) => {
  return await del(`${API.CA_REPORT}/${reportId}/notes/${noteId}`);
};
export const createLineItem = async (reportId: string, lineItemData: CreateLineItemDTO) => {
  return await post(`${API.CA_REPORT}/${reportId}/create-line-item`, lineItemData);
};
export const bulkDeleteReports = async (ids: readonly GridRowId[]) => {
  return await del(`${API.CA_REPORT}/bulk-delete`, {
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(ids),
  });
};

export const getCompsDivisionAverages = async (reportId: string, divisionId: string) => {
  return (await get(`${API.CA_REPORT}/${reportId}/comps/divisions/${divisionId}/budgetPerDivision`)) as CompsDivisionAveragesDTO[];
};
export const generateCompProjectAnalysis = async (inputDTO: generateReportCompsDTO) => {
  return await post('comp-project-analyses', inputDTO);
};
export const deleteReportComp = async (compId: string) => {
  return (await del(`comp-project-analyses/${compId}`)) as string;
};
export const createNoteForReportLineItem = async (reportLineItemId: string, noteDto: NoteDTO) => {
  return (await put(`${API.CA_REPORT_LINE_ITEM}/${reportLineItemId}/add-note`, noteDto)) as ReportLineItem;
};
export const deleteNoteFromLineItem = async (reportLineItemId: string) => {
  return (await del(`${API.CA_REPORT_LINE_ITEM}/${reportLineItemId}/delete-note`)) as string;
};
export const copyReport = async (reportId: string, reportName: string) => {
  const requestBody = { reportName };
  return await post(`${API.CA_REPORT}/Copy/${reportId}`, requestBody);
};
export const addAllCompsPricing = async (reportId: string, lineItemData: CreateLineItemDTO[]) => {
  return await post(`${API.CA_REPORT}/${reportId}/add-all-comps-pricing`, lineItemData);
};

// Invoice API functions for Cost Analysis Reports
export const createCAReportInvoice = async (createDto: CreateCAReportInvoiceDTO) => {
  return (await post(`${API.CA_REPORT}/invoices`, createDto)) as CAReportInvoiceDTO;
};

export const getCAReportInvoices = async (caReportId: string) => {
  return (await get(`${API.CA_REPORT}/${caReportId}/invoices`)) as CAReportInvoiceDTO[];
};

export const getCAReportInvoice = async (invoiceId: string) => {
  return (await get(`${API.CA_REPORT}/invoices/${invoiceId}`)) as CAReportInvoiceDTO;
};

export const updateCAReportInvoice = async (invoiceId: string, updateDto: UpdateCAReportInvoiceDTO) => {
  return (await put(`${API.CA_REPORT}/invoices/${invoiceId}`, updateDto)) as CAReportInvoiceDTO;
};

export const deleteCAReportInvoice = async (invoiceId: string) => {
  return (await del(`${API.CA_REPORT}/invoices/${invoiceId}`)) as string;
};
